import { sendToExtension } from '../extensionHandshake';
import { refreshWithCognito } from './cognitoRefresh';
import { clearSession, getTokens, isIdTokenExpired } from './session';

/**
 * How handing the session to the extension ended on a hard load.
 * - `signed_out`: Cognito refused the refresh token. The session has been
 *   cleared here and nothing was sent; the caller marks the app signed out.
 * - `skipped`: nothing was sent and the session is untouched (no usable id
 *   token, or Cognito could not be reached).
 * - `sent`: the freshly refreshed tokens went to the extension; `answered` is
 *   whether it replied.
 */
export type ExtensionSyncOutcome =
  | { kind: 'signed_out' }
  | { kind: 'skipped' }
  | { kind: 'sent'; answered: boolean };

/**
 * Refresh the session with Cognito, then hand the extension the pair Cognito
 * just issued.
 *
 * The refresh comes first because the id token on a hard load is usually still
 * good for hours while the refresh token behind it may be long dead. Pushing
 * that pair gave the extension a session that looked fine and could never
 * renew. A refusal here is the user's real sign-out: the stored tokens go and
 * the extension is told nothing, so a refresh token Cognito has refused is never
 * handed on. A refresh that could not reach Cognito proves nothing about the
 * token, so the session stays and nothing is pushed until the next load.
 *
 * The refresh token rides along because the extension mints the desktop
 * handoff from its own session, and needs something the backend can trade for
 * the access token it never stores.
 *
 * Both the clear and the push are guarded by the stored refresh token still
 * being the one that was refreshed, so a sign-in landing meanwhile (the OAuth
 * callback stores its tokens as the app loads) is never wiped or overwritten.
 */
export async function refreshAndSendSessionToExtension(): Promise<ExtensionSyncOutcome> {
  const { idToken, refreshToken: refreshedToken } = getTokens();
  // An expired id token is what a page load made offline keeps (see
  // bootstrapAuth); the extension renews its own, so it is not sent.
  if (!idToken || isIdTokenExpired(idToken)) {
    return { kind: 'skipped' };
  }

  const refreshed = await refreshWithCognito();
  if (refreshed.kind === 'refused') {
    // A sign-in that landed while Cognito was answering replaced the refresh
    // token we asked about; its session is not ours to clear.
    if (getTokens().refreshToken !== refreshedToken) {
      return { kind: 'skipped' };
    }
    clearSession();
    return { kind: 'signed_out' };
  }
  if (refreshed.kind === 'network') {
    return { kind: 'skipped' };
  }

  const { idToken: freshIdToken, refreshToken } = refreshed.tokens;
  const answered = await sendToExtension({
    idToken: freshIdToken,
    refreshToken,
  });
  return { kind: 'sent', answered };
}
